import type { IFarm } from 'interfaces/IFarm';
import type { GUnit, SeedSpeciesProductionKey } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import {
  InputStepType,
  type IInputStep,
  type IInputStepProduction,
  type IInputStepSoilInput,
} from 'interfaces/IInputStep';
import type { IInputTask, InputTaskCategory } from 'interfaces/IInputTask';
import {
  getInputDisplayName,
  getInputYear,
  indexInputsByPaddock,
} from 'utils/FarmUtils';
import {
  ANALYSIS_PRODUCTION_LOW,
  ANALYSIS_PRODUCTION_POTENTIAL,
  NUTRITION_PADDOCK_YEAR_LABEL,
} from 'constants/uiConstants';
import { convertToHectares, convertToTonnes } from 'utils/unitConversion';
import type { AnalysisProductionScenario } from '../../../../../redux/slices/InsightsSlice';
import {
  computeStepNutrientKg,
  emptyTally,
  NUTRIENT_DISPLAY_ORDER,
  resolvePlanAreaHa,
  type NutrientKey,
  type NutritionTally,
} from '../computeLiveTotals';
import { resolveAreaHa } from '../insightUtils';

/* ═══════════════════════════════════════════════════════════════
   Removal keys
   ═══════════════════════════════════════════════════════════════ */

/**
 * The production-step field holding each nutrient's removal value (copied
 * from the crop when the step is created, OP-2467), or null where crops
 * don't record one — Ni and Co have no product* field, so their balance
 * can't be judged.
 */
export const NUTRIENT_REMOVAL_KEY: Record<
  NutrientKey,
  SeedSpeciesProductionKey | null
> = {
  nutrientN: 'productN',
  nutrientP: 'productP',
  nutrientK: 'productK',
  nutrientS: 'productS',
  nutrientCa: 'productCa',
  nutrientMg: 'productMg',
  nutrientNa: 'productNa',
  nutrientB: 'productB',
  nutrientCu: 'productCu',
  nutrientFe: 'productFe',
  nutrientMn: 'productMn',
  nutrientZn: 'productZn',
  nutrientMo: 'productMo',
  nutrientCl: 'productCl',
  nutrientSi: 'productSi',
  nutrientNi: null,
  nutrientCo: null,
};

/* ═══════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════ */

export type NutrientStatus =
  | 'building'
  | 'declining'
  | 'balanced'
  /** Nothing applied and nothing removed. */
  | 'noData'
  /** Applied, but crops record no removal for this nutrient (Ni, Co). */
  | 'noRemovalData';

/** One soil input step as a line in the audit, with the nutrients it supplied. */
export interface NutritionProductLine {
  key: string;
  inputId: string;
  taskId: string;
  stepId: string;
  productName: string;
  /** Rate as entered, per ha. */
  rate: number;
  /** Rate unit as entered, e.g. "kg" or "L". Null when not recorded. */
  rateUnit: string | null;
  /** Area this line was applied to (ha) — less than planAreaHa when it went on part of the paddock. */
  areaHa: number;
  /** The plan's own area (ha), which perHa is averaged over. */
  planAreaHa: number;
  /** Where it went on, for messages, e.g. "North paddock, 2025". */
  location: string;
  /** Application date: the product's own date, or failing that its task's. Null when neither is set. */
  date: string | null;
  /** False when the date is a month only (a task date entered without a day). */
  dateHasDay: boolean;
  /** Nutrients supplied, in kg/ha of the plan's planted area. */
  perHa: Partial<NutritionTally>;
  /** The product's nutrient analysis as recorded on the plan (%), non-zero nutrients only. */
  analysis: Partial<NutritionTally>;
}

/** A harvest yield recorded on a plan's production, as entered. */
export interface NutritionYield {
  value: number;
  /** Yield unit as entered, e.g. "t/ha" or "t". Null when not recorded. */
  unit: string | null;
}

/** One plan in a year — its crop, products and crop removal, per ha of the plan's own area. */
export interface NutritionPlanAudit {
  inputId: string;
  cropName: string;
  /** The plan's own area (ha) — zone area, or the paddock's when the plan has none. */
  areaHa: number;
  /** Each production's yield in the requested category and yield scenario. */
  yields: NutritionYield[];
  products: NutritionProductLine[];
  /** kg/ha of the plan's area. */
  applied: NutritionTally;
  removed: NutritionTally;
  /** True when the plan has no production with a yield and nutrient values. */
  removalMissing: boolean;
}

/**
 * One paddock's year — every plan starting that year. Two plans can be
 * different crops on different ground (a split paddock) or one after the
 * other on the same ground (double cropping); see resolveYearAreaHa.
 */
export interface NutritionSeason {
  season: number;
  /** Area cropped this year (ha) — see resolveYearAreaHa. */
  areaHa: number;
  plans: NutritionPlanAudit[];
  /** Every plan's crop, in plan order. */
  cropNames: string[];
  /** Every plan's yields, in plan order. */
  yields: NutritionYield[];
  /** The year as a whole, kg/ha of areaHa. */
  applied: NutritionTally;
  removed: NutritionTally;
  balance: NutritionTally;
  /** True when any plan this year has no production with a yield and nutrient values. */
  removalMissing: boolean;
}

export interface PaddockNutritionAudit {
  paddockId: string;
  paddockName: string;
  farmId: string;
  farmName: string;
  /** Planted area in the latest year in range with a plan (ha); 0 when none. */
  areaHa: number;
  /** Average planted area across the years with a plan (ha) — weights the paddock in area-weighted totals. */
  averageAreaHa: number;
  /** True when planted area changes from year to year in range. */
  areaVaries: boolean;
  /** Seasons in range that have a plan, oldest first. Empty when the paddock has no plan in range. */
  seasons: NutritionSeason[];
  /** Range totals, kg/ha. */
  applied: NutritionTally;
  removed: NutritionTally;
  balance: NutritionTally;
  missingRemovalSeasons: number[];
}

export interface AreaWeightedBalance {
  /** Planted area of the paddocks that have a plan in range (ha), each at its average. */
  areaHa: number;
  /** Each paddock's range balance weighted by its average planted area, kg/ha. */
  balance: NutritionTally;
}

export interface FarmNutritionGroup extends AreaWeightedBalance {
  farmId: string;
  farmName: string;
  paddocks: PaddockNutritionAudit[];
}

export interface NutritionAudit {
  seasons: number[];
  farms: FarmNutritionGroup[];
  total: AreaWeightedBalance;
}

/* ═══════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════ */

/**
 * Status of one nutrient's balance. No tolerance band — under is declining,
 * over is building (OP-2508). Rounded to 0.1 kg/ha first, the precision
 * shown, so floating-point noise around zero never flips the status.
 */
export const resolveNutrientStatus = (
  key: NutrientKey,
  applied: number,
  removed: number,
  balance: number
): NutrientStatus => {
  if (!NUTRIENT_REMOVAL_KEY[key]) return applied > 0 ? 'noRemovalData' : 'noData';
  if (applied <= 0 && removed <= 0) return 'noData';
  const rounded = Math.round(balance * 10) / 10;
  if (rounded < 0) return 'declining';
  if (rounded > 0) return 'building';
  return 'balanced';
};

/**
 * The production's yield for a scenario — low, expected (the production's
 * own yield) or high. Actual production records one yield, so callers pass
 * the expected scenario for Actual.
 */
export const resolveScenarioYield = (
  production: IInputStepProduction,
  scenario: AnalysisProductionScenario
): number | null => {
  if (scenario === ANALYSIS_PRODUCTION_LOW) return production.yieldLow;
  if (scenario === ANALYSIS_PRODUCTION_POTENTIAL) return production.yieldHigh;
  return production.yield;
};

/**
 * Nutrient kg removed by one production step, or null when it can't be
 * worked out (no yield, no area, or no nutrient values).
 *
 * ── Formula ──────────────────────────────────────────────────────────────────
 * product* fields store the nutrient as a percentage of the harvested product.
 *   removed_kg = yield_t_per_ha × 1000 × (pct / 100) × areaHa
 * yield is converted to t/ha from yieldUnit (unrecognised → raw value).
 * Harvested product only — residue (stubble) is not counted (OP-2509).
 */
export const computeStepRemovalKg = (
  step: IInputStep,
  task: IInputTask,
  input: IInput,
  gUnits: GUnit[],
  scenario: AnalysisProductionScenario
): Partial<NutritionTally> | null => {
  if (step.stepType !== InputStepType.Production) return null;
  const s = step as IInputStepProduction;

  const yieldRaw = resolveScenarioYield(s, scenario) ?? 0;
  if (yieldRaw <= 0) return null;
  const areaHa = resolveAreaHa(step, task, input, gUnits);
  if (areaHa <= 0) return null;

  const yieldKgPerHa =
    (convertToTonnes(yieldRaw, s.yieldUnit, gUnits) ?? yieldRaw) * 1000;

  const kg: Partial<NutritionTally> = {};
  let hasValues = false;
  for (const key of NUTRIENT_DISPLAY_ORDER) {
    const removalKey = NUTRIENT_REMOVAL_KEY[key];
    if (!removalKey) continue;
    const pct = s[removalKey];
    if (pct == null) continue;
    hasValues = true;
    kg[key] = yieldKgPerHa * (pct / 100) * areaHa;
  }
  return hasValues ? kg : null;
};

const subtractTally = (a: NutritionTally, b: NutritionTally): NutritionTally => {
  const result = emptyTally();
  for (const key of NUTRIENT_DISPLAY_ORDER) result[key] = a[key] - b[key];
  return result;
};

const addInto = (target: NutritionTally, source: Partial<NutritionTally>, divisor = 1) => {
  for (const key of NUTRIENT_DISPLAY_ORDER) {
    target[key] += (source[key] ?? 0) / divisor;
  }
};

const areaWeighted = (paddocks: PaddockNutritionAudit[]): AreaWeightedBalance => {
  const balance = emptyTally();
  let areaHa = 0;
  for (const paddock of paddocks) {
    if (paddock.seasons.length === 0 || paddock.averageAreaHa <= 0) continue;
    areaHa += paddock.averageAreaHa;
    for (const key of NUTRIENT_DISPLAY_ORDER) {
      balance[key] += paddock.balance[key] * paddock.averageAreaHa;
    }
  }
  if (areaHa > 0) {
    for (const key of NUTRIENT_DISPLAY_ORDER) balance[key] /= areaHa;
  }
  return { areaHa, balance };
};

interface PaddockMeta {
  paddockName: string;
  farmId: string;
  farmName: string;
  areaHa: number;
}

const indexPaddocks = (farms: IFarm[], gUnits: GUnit[]): Map<string, PaddockMeta> => {
  const meta = new Map<string, PaddockMeta>();
  for (const farm of farms) {
    for (const paddock of farm.paddocks ?? []) {
      if (paddock.isExcluded) continue;
      const area = paddock.paddockArea ?? 0;
      meta.set(paddock.paddockId, {
        paddockName: paddock.paddockName,
        farmId: farm.id,
        farmName: farm.farmName,
        areaHa:
          area > 0
            ? (convertToHectares(area, paddock.areaUnit, gUnits) ?? area)
            : 0,
      });
    }
  }
  return meta;
};

/** Leeway for area rounding when deciding whether a year's plans fit on the paddock side by side. */
const SPLIT_PADDOCK_TOLERANCE = 1.05;

/**
 * The area a year's plans cover. Plans that together fit within the paddock
 * are taken to be on different ground (a split paddock — e.g. 30 ha wheat
 * and 24 ha barley on a 54 ha paddock), so their areas add. Otherwise they
 * share ground (double cropping — e.g. winter wheat then a summer crop on
 * the whole paddock), so it's the largest plan's area. One plan is simply
 * its own area.
 */
export const resolveYearAreaHa = (
  planAreas: number[],
  paddockAreaHa: number
): number => {
  const largest = Math.max(0, ...planAreas);
  if (planAreas.length < 2 || paddockAreaHa <= 0) return largest;
  const total = planAreas.reduce((sum, area) => sum + area, 0);
  return total <= paddockAreaHa * SPLIT_PADDOCK_TOLERANCE ? total : largest;
};

/* ═══════════════════════════════════════════════════════════════
   Audit
   ═══════════════════════════════════════════════════════════════ */

interface ComputeNutritionAuditArgs {
  /** Plans in tree scope, across every year — not filtered to the tree year. */
  inputs: IInput[];
  /** Visible farms only (useVisibleOrgData) — paddocks not in here are skipped. */
  farms: IFarm[];
  gUnits: GUnit[];
  categories: Set<InputTaskCategory>;
  seasons: number[];
  /** Which yield removal is worked out from — expected for Actual. */
  yieldScenario: AnalysisProductionScenario;
}

/**
 * Nutrients applied and removed per paddock and season, in kg/ha, with the
 * balance (applied − removed) for each season and across the range.
 *
 * Applied uses the Live totals formula (computeStepNutrientKg). Each plan's
 * figures are per ha of its own area (zone area, falling back to the
 * paddock's), so part-paddock applications count pro rata. A year's figures
 * are every plan's kg over the year's area (resolveYearAreaHa): plans on the
 * same ground (double cropping) add up, plans side by side (a split
 * paddock) average by area.
 *
 * Only paddocks with at least one plan in scope (any year) get a row; a
 * paddock whose plans all fall outside the range keeps its row with no
 * seasons, so the grid can say why it's empty.
 *
 * Pure function — no side effects, no hooks, no Redux.
 */
export const computeNutritionAudit = ({
  inputs,
  farms,
  gUnits,
  categories,
  seasons,
  yieldScenario,
}: ComputeNutritionAuditArgs): NutritionAudit => {
  const seasonSet = new Set(seasons);
  const paddockMeta = indexPaddocks(farms, gUnits);
  const byPaddock = indexInputsByPaddock(inputs);

  const paddocks: PaddockNutritionAudit[] = [];

  byPaddock.forEach((paddockInputs, paddockId) => {
    const meta = paddockMeta.get(paddockId);
    if (!meta) return;

    // Each plan's totals in kg, kept until its year's area is known.
    interface PlanWork {
      plan: NutritionPlanAudit;
      appliedKg: NutritionTally;
      removedKg: NutritionTally;
    }
    const plansByYear = new Map<number, PlanWork[]>();

    for (const input of paddockInputs) {
      const year = getInputYear(input);
      if (year == null || !seasonSet.has(year)) continue;

      const planAreaHa = resolvePlanAreaHa(input, gUnits) || meta.areaHa;
      if (planAreaHa <= 0) continue;

      const work: PlanWork = {
        plan: {
          inputId: input.id,
          cropName: getInputDisplayName(input),
          areaHa: planAreaHa,
          yields: [],
          products: [],
          applied: emptyTally(),
          removed: emptyTally(),
          removalMissing: false,
        },
        appliedKg: emptyTally(),
        removedKg: emptyTally(),
      };
      const { plan } = work;

      let hasRemoval = false;
      for (const task of input.tasks ?? []) {
        if (!categories.has(task.category)) continue;
        for (const step of task.steps ?? []) {
          const contribution = computeStepNutrientKg(step, task, input, gUnits);
          if (contribution) {
            addInto(work.appliedKg, contribution.kg);
            const perHa: Partial<NutritionTally> = {};
            for (const key of NUTRIENT_DISPLAY_ORDER) {
              const kg = contribution.kg[key];
              if (kg != null) perHa[key] = kg / planAreaHa;
            }
            const s = step as unknown as IInputStepSoilInput;
            const analysis: Partial<NutritionTally> = {};
            for (const key of NUTRIENT_DISPLAY_ORDER) {
              const pct = s[key] ?? 0;
              if (pct > 0) analysis[key] = pct;
            }
            plan.products.push({
              key: `${input.id}:${task.taskId}:${step.stepId}`,
              inputId: input.id,
              taskId: task.taskId,
              stepId: step.stepId,
              productName: s.item,
              rate: s.rate ?? 0,
              rateUnit: s.appUnit?.unit ?? null,
              areaHa: contribution.areaHa,
              planAreaHa,
              location: NUTRITION_PADDOCK_YEAR_LABEL(meta.paddockName, year),
              date: step.endDate ?? task.endDate ?? null,
              dateHasDay: step.endDate ? true : task.endDateHasDay,
              perHa,
              analysis,
            });
            continue;
          }

          if (step.stepType === InputStepType.Production) {
            const production = step as IInputStepProduction;
            const scenarioYield = resolveScenarioYield(production, yieldScenario);
            if (scenarioYield != null && scenarioYield > 0) {
              plan.yields.push({
                value: scenarioYield,
                unit: production.yieldUnit?.unit ?? null,
              });
            }
          }
          const removedKg = computeStepRemovalKg(
            step,
            task,
            input,
            gUnits,
            yieldScenario
          );
          if (removedKg) {
            hasRemoval = true;
            addInto(work.removedKg, removedKg);
          }
        }
      }
      // In application order, as they'd go on; undated last. The sort is
      // stable, so products on the same date keep the order they were entered.
      plan.products.sort((a, b) => {
        if (a.date === b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return a.date.localeCompare(b.date);
      });
      plan.removalMissing = !hasRemoval;
      addInto(plan.applied, work.appliedKg, planAreaHa);
      addInto(plan.removed, work.removedKg, planAreaHa);

      const yearPlans = plansByYear.get(year) ?? [];
      yearPlans.push(work);
      plansByYear.set(year, yearPlans);
    }

    const seasonMap = new Map<number, NutritionSeason>();
    plansByYear.forEach((yearPlans, year) => {
      const areaHa = resolveYearAreaHa(
        yearPlans.map((w) => w.plan.areaHa),
        meta.areaHa
      );
      const applied = emptyTally();
      const removed = emptyTally();
      for (const work of yearPlans) {
        addInto(applied, work.appliedKg, areaHa);
        addInto(removed, work.removedKg, areaHa);
      }
      const plans = yearPlans.map((w) => w.plan);
      seasonMap.set(year, {
        season: year,
        areaHa,
        plans,
        cropNames: plans.map((p) => p.cropName),
        yields: plans.flatMap((p) => p.yields),
        applied,
        removed,
        balance: emptyTally(),
        removalMissing: plans.some((p) => p.removalMissing),
      });
    });

    const paddockSeasons = Array.from(seasonMap.values()).sort(
      (a, b) => a.season - b.season
    );
    const applied = emptyTally();
    const removed = emptyTally();
    for (const season of paddockSeasons) {
      season.balance = subtractTally(season.applied, season.removed);
      addInto(applied, season.applied);
      addInto(removed, season.removed);
    }

    const seasonAreas = paddockSeasons.map((s) => s.areaHa);
    const latestArea = seasonAreas[seasonAreas.length - 1] ?? 0;
    const averageArea =
      seasonAreas.length > 0
        ? seasonAreas.reduce((sum, area) => sum + area, 0) / seasonAreas.length
        : 0;

    paddocks.push({
      paddockId,
      paddockName: meta.paddockName,
      farmId: meta.farmId,
      farmName: meta.farmName,
      areaHa: latestArea,
      averageAreaHa: averageArea,
      // Compared at the 0.1 ha shown, so unit-conversion noise doesn't count.
      areaVaries: new Set(seasonAreas.map((a) => Math.round(a * 10))).size > 1,
      seasons: paddockSeasons,
      applied,
      removed,
      balance: subtractTally(applied, removed),
      missingRemovalSeasons: paddockSeasons
        .filter((s) => s.removalMissing)
        .map((s) => s.season),
    });
  });

  const byName = (a: string, b: string) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' });

  const farmMap = new Map<string, FarmNutritionGroup>();
  for (const paddock of paddocks) {
    const group = farmMap.get(paddock.farmId) ?? {
      farmId: paddock.farmId,
      farmName: paddock.farmName,
      paddocks: [],
      areaHa: 0,
      balance: emptyTally(),
    };
    group.paddocks.push(paddock);
    farmMap.set(paddock.farmId, group);
  }

  const farmGroups = Array.from(farmMap.values())
    .map((group) => ({
      ...group,
      ...areaWeighted(group.paddocks),
      paddocks: [...group.paddocks].sort((a, b) =>
        byName(a.paddockName, b.paddockName)
      ),
    }))
    .sort((a, b) => byName(a.farmName, b.farmName));

  return {
    seasons,
    farms: farmGroups,
    total: areaWeighted(paddocks),
  };
};

/* ═══════════════════════════════════════════════════════════════
   Season totals (charts)
   ═══════════════════════════════════════════════════════════════ */

export interface NutritionSeasonTotals {
  season: number;
  /** Area of the paddocks with a plan this season (ha). 0 when none. */
  areaHa: number;
  /** kg/ha, weighted by planted area across the paddocks with a plan this season. */
  applied: NutritionTally;
  removed: NutritionTally;
}

/**
 * Applied and removed per season across the given paddocks, each weighted by
 * that year's planted area — for one paddock this is simply its own seasons. A season
 * no paddock has a plan in comes back with zero area and empty tallies, so
 * charts keep one slot per season in range.
 */
export const computeSeasonTotals = (
  paddocks: PaddockNutritionAudit[],
  seasons: number[]
): NutritionSeasonTotals[] =>
  seasons.map((year) => {
    const applied = emptyTally();
    const removed = emptyTally();
    let areaHa = 0;
    for (const paddock of paddocks) {
      const season = paddock.seasons.find((s) => s.season === year);
      if (!season || season.areaHa <= 0) continue;
      areaHa += season.areaHa;
      for (const key of NUTRIENT_DISPLAY_ORDER) {
        applied[key] += season.applied[key] * season.areaHa;
        removed[key] += season.removed[key] * season.areaHa;
      }
    }
    if (areaHa > 0) {
      for (const key of NUTRIENT_DISPLAY_ORDER) {
        applied[key] /= areaHa;
        removed[key] /= areaHa;
      }
    }
    return { season: year, areaHa, applied, removed };
  });
